import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "../components/sections/Navbar";
import { Footer } from "../components/sections/Footer";
import { ScrollProgress } from "../components/visuals/ScrollProgress";
import useReveal from "../lib/useReveal";

const EFFECTIVE_DATE = "September 16, 2026";

export default function PrivacyPolicyPage() {
    useReveal();
    return (
        <main data-testid="privacy-policy-page" className="min-h-screen bg-white">
            <ScrollProgress />
            <Navbar />

            {/* Hero */}
            <section className="relative bg-[#0A1428] text-white pt-32 md:pt-40 pb-16 md:pb-20 overflow-hidden">
                <div className="absolute inset-0 dot-grid-light opacity-25 pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <Link
                        to="/"
                        data-testid="privacy-back"
                        className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-slate-300 hover:text-white transition-colors mb-10"
                    >
                        <ArrowLeft size={16} strokeWidth={2} />
                        Back to Kairos
                    </Link>
                    <div className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                        Legal
                    </div>
                    <h1 className="mt-6 font-display font-extrabold text-4xl md:text-6xl leading-[1] tracking-tighter text-balance max-w-3xl">
                        Privacy Policy.
                    </h1>
                    <p className="mt-6 text-sm md:text-base text-slate-400 font-medium">
                        Effective {EFFECTIVE_DATE}
                    </p>
                </div>
            </section>

            {/* Body */}
            <section className="relative bg-white py-20 md:py-24">
                <div className="max-w-3xl mx-auto px-6 md:px-12 text-slate-700 font-medium leading-relaxed text-[15px] md:text-base space-y-10">
                    <p className="reveal">
                        Kairos ORD LLC (&ldquo;Kairos,&rdquo; &ldquo;we,&rdquo;
                        &ldquo;us,&rdquo; or &ldquo;our&rdquo;) respects your
                        privacy. This Privacy Policy describes how we collect,
                        use, and protect information when you visit{" "}
                        <a href="https://kairosord.org" className="text-[#F0493E] font-bold link-underline">kairosord.org</a>{" "}
                        (the &ldquo;Site&rdquo;), interact with our forms, or
                        respond to our advertising — including advertising on
                        LinkedIn, Google, and other platforms.
                    </p>

                    <Section
                        title="1. Information We Collect"
                        testid="privacy-section-info"
                    >
                        <p>
                            <span className="font-bold text-[#0A1428]">
                                Information you provide directly.
                            </span>{" "}
                            When you submit our contact form, pilot
                            application, or any inquiry form on the Site, we
                            collect the information you supply — typically
                            your name, email address, organization,
                            role/facility type, the product or category
                            you&apos;re interested in, and the contents of
                            your message.
                        </p>
                        <p>
                            <span className="font-bold text-[#0A1428]">
                                Information collected automatically.
                            </span>{" "}
                            When you visit the Site, we automatically collect
                            certain technical information, including your IP
                            address, browser type, device information, pages
                            viewed, referring URL, and general engagement
                            (clicks, scrolls, session duration). This is
                            gathered through cookies, pixels, and similar
                            technologies used by our analytics and
                            advertising partners (see Section 4).
                        </p>
                        <p>
                            <span className="font-bold text-[#0A1428]">
                                We do not knowingly collect
                            </span>{" "}
                            payment card numbers, government IDs, or health
                            records through this Site.
                        </p>
                    </Section>

                    <Section
                        title="2. How We Use Information"
                        testid="privacy-section-use"
                    >
                        <p>We use the information we collect to:</p>
                        <ul className="mt-4 space-y-2.5 list-disc pl-5 marker:text-[#F0493E]">
                            <li>Respond to inquiries, pilot applications, and quote requests.</li>
                            <li>Deliver, operate, and improve the Site and our products.</li>
                            <li>Send transactional and follow-up communications related to your inquiry.</li>
                            <li>Measure and improve the performance of our advertising and marketing campaigns.</li>
                            <li>Comply with legal obligations, prevent fraud, and enforce our terms.</li>
                        </ul>
                    </Section>

                    <Section
                        title="3. Legal Bases (EU / UK Visitors)"
                        testid="privacy-section-legal"
                    >
                        <p>
                            If you are located in the European Economic Area
                            or the United Kingdom, our legal bases for
                            processing your personal data are: your consent
                            (for cookies and marketing communications), our
                            legitimate interest in operating and promoting
                            our business, performance of a contract or
                            steps taken at your request, and compliance with
                            legal obligations.
                        </p>
                    </Section>

                    <Section
                        title="4. Cookies, Analytics & Advertising"
                        testid="privacy-section-cookies"
                    >
                        <p>
                            We use cookies and similar technologies to
                            understand how visitors use the Site and to
                            measure the effectiveness of our marketing. Our
                            current providers are:
                        </p>
                        <ul className="mt-4 space-y-2.5 list-disc pl-5 marker:text-[#F0493E]">
                            <li>
                                <span className="font-bold text-[#0A1428]">
                                    PostHog
                                </span>{" "}
                                — product analytics and session
                                recording. May capture pages viewed,
                                interactions, and anonymized session
                                playback for user-experience improvement.
                            </li>
                            <li>
                                <span className="font-bold text-[#0A1428]">
                                    LinkedIn Insight Tag
                                </span>{" "}
                                — used to measure the effectiveness of
                                LinkedIn advertising campaigns, build
                                audiences, and understand which content
                                visitors engage with. LinkedIn may combine
                                this information with data it already
                                holds about you.
                            </li>
                            <li>
                                <span className="font-bold text-[#0A1428]">
                                    Other advertising and measurement
                                    partners
                                </span>{" "}
                                may be added over time (e.g., Google Ads,
                                Meta). If added, they will be disclosed
                                here.
                            </li>
                        </ul>
                        <p className="mt-4">
                            You can control cookies through your browser
                            settings. Blocking analytics or advertising
                            cookies will not affect the core functionality
                            of the Site.
                        </p>
                    </Section>

                    <Section
                        title="5. Service Providers We Share Information With"
                        testid="privacy-section-sharing"
                    >
                        <p>
                            We do not sell your personal information. We
                            share limited information with vetted service
                            providers that help us operate the Site and
                            respond to inquiries:
                        </p>
                        <ul className="mt-4 space-y-2.5 list-disc pl-5 marker:text-[#F0493E]">
                            <li>
                                <span className="font-bold text-[#0A1428]">Resend</span>{" "}
                                — transactional email delivery for form
                                submissions.
                            </li>
                            <li>
                                <span className="font-bold text-[#0A1428]">MongoDB Atlas</span>{" "}
                                — encrypted database hosting for form
                                submissions and internal records.
                            </li>
                            <li>
                                <span className="font-bold text-[#0A1428]">PostHog</span>{" "}
                                — analytics and session-recording
                                processing.
                            </li>
                            <li>
                                <span className="font-bold text-[#0A1428]">
                                    Advertising platforms
                                </span>{" "}
                                — LinkedIn, and any others disclosed in
                                Section 4, for measurement and campaign
                                optimization.
                            </li>
                        </ul>
                        <p className="mt-4">
                            We may also disclose information if required by
                            law, subpoena, or to protect the rights,
                            property, or safety of Kairos, our users, or
                            others.
                        </p>
                    </Section>

                    <Section
                        title="6. Data Retention"
                        testid="privacy-section-retention"
                    >
                        <p>
                            We retain inquiry information for as long as
                            reasonably necessary to respond to your request,
                            fulfill the purposes described in this Policy,
                            and comply with legal obligations. Analytics data
                            is retained per the retention settings of each
                            provider (typically 12–24 months).
                        </p>
                    </Section>

                    <Section
                        title="7. Your Rights"
                        testid="privacy-section-rights"
                    >
                        <p>
                            Depending on where you live, you may have the
                            right to access, correct, delete, or port your
                            personal information, and to opt out of certain
                            uses of it — including targeted advertising and
                            sale/sharing (as those terms are defined by
                            applicable state law).
                        </p>
                        <p className="mt-4">
                            To make a request, email us at{" "}
                            <a href="mailto:kairosord@gmail.com" className="text-[#F0493E] font-bold link-underline">
                                kairosord@gmail.com
                            </a>
                            . We will respond within the time frame required
                            by applicable law.
                        </p>
                    </Section>

                    <Section
                        title="8. Security"
                        testid="privacy-section-security"
                    >
                        <p>
                            We use commercially reasonable technical and
                            organizational measures to protect the
                            information we collect, including TLS
                            encryption in transit and encryption at rest for
                            stored records. No method of transmission or
                            storage is 100% secure, so we cannot guarantee
                            absolute security.
                        </p>
                    </Section>

                    <Section
                        title="9. Children's Privacy"
                        testid="privacy-section-children"
                    >
                        <p>
                            The Site is not directed to children under 16.
                            We do not knowingly collect personal information
                            from children. If you believe a child has
                            provided us with information, please contact us
                            and we will delete it.
                        </p>
                    </Section>

                    <Section
                        title="10. Changes to This Policy"
                        testid="privacy-section-changes"
                    >
                        <p>
                            We may update this Privacy Policy from time to
                            time. When we do, we will revise the &ldquo;
                            Effective&rdquo; date above. For material
                            changes, we will provide additional notice on
                            the Site.
                        </p>
                    </Section>

                    <Section
                        title="11. Contact Us"
                        testid="privacy-section-contact"
                    >
                        <p>
                            If you have any questions about this Privacy
                            Policy or our data practices, please contact:
                        </p>
                        <div className="mt-4 border border-slate-200 p-6 bg-slate-50">
                            <div className="font-display font-extrabold text-[#0A1428] text-lg">
                                Kairos ORD LLC
                            </div>
                            <div className="mt-2 text-sm text-slate-600">
                                <a href="mailto:kairosord@gmail.com" className="text-[#F0493E] font-bold link-underline">
                                    kairosord@gmail.com
                                </a>
                            </div>
                            <div className="mt-1 text-sm text-slate-600">
                                United States
                            </div>
                        </div>
                    </Section>
                </div>
            </section>

            <Footer />
        </main>
    );
}

const Section = ({ title, testid, children }) => (
    <div className="reveal space-y-4" data-testid={testid}>
        <h2 className="font-display font-extrabold text-2xl md:text-3xl text-[#0A1428] tracking-tight leading-tight">
            {title}
        </h2>
        {children}
    </div>
);
